import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { SubagentAccess } from 'js/src/agent/subagents/subagentAccess';
import chatMessageTextStyles from 'css/src/components/agent/ChatMessageText.module.scss';

import React from 'react';
import type { TimeAwareMediaAsset } from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';

import { z } from 'zod';

import MarkdownWithDiff from '../components/MarkdownWithDiff';
import { MediaRegistry } from '../registries/MediaRegistry';
import type { CustomRendererComponentProps, ToolExecutionContext } from '../types';

import { nonEditableAssetError } from './non-editable-asset';
import { resolveSceneHandle } from './scene-addressing';
import type { ToolConstructorArgs } from './Tool';
import { VideoStateTool } from './VideoStateTool';

import { isNonRecommendable } from 'js/src/agent/video-state/serialization/assetSerializer';
import {
    describeAspectRatio,
    getMediaFrameFit,
    getMediaUuidFromAsset,
} from 'js/src/agent/video-state/serialization/mediaHelpers';
import BackgroundAsset from 'js/src/components/app/BackgroundAsset';
import { refreshFirstScenePreviewOperation } from 'js/src/data/video-planner';
import { makeOperationsCallable } from 'js/src/luminary/luminary-hooks';
import { fetchAndSetRecommendedSceneAttributesOperation } from 'js/src/luminary/luminary-operations';

export const setMediaSchema = z.object({
    scene_id: z.string().describe('The scene_id from the video state.'),
    asset_name: z.string().describe('The asset name (e.g. media_1)'),
    media_id: z
        .string()
        .describe('The ID of the media to apply to the asset. Must be an ID from search results.'),
});
export type SetMediaArgs = z.infer<typeof setMediaSchema>;

export interface SetMediaToolConstructorArgs extends ToolConstructorArgs {
    /**
     * When true (default), changing a scene's media fetches and applies recommended
     * scene attributes (crop, brightness, Ken Burns, etc.) inline, matching a manual
     * media swap. Set to false for bulk flows like the Media Optimizer, which apply
     * recommendations in a single batch at the end of the run to avoid spawning a
     * WebGL context per scene (which can cause context-loss errors).
     */
    recommendSceneAttributes?: boolean;
}

export class SetMediaTool extends VideoStateTool<
    typeof setMediaSchema,
    SetMediaToolConstructorArgs
> {
    protected override readonly schema = setMediaSchema;
    protected override readonly toolName = TOOL_NAMES.setMedia;
    public override readonly subagentAccess = SubagentAccess.EDITS_TARGET;
    protected override readonly toolDescription = `Put a media clip into a specific scene and asset slot (replaces whatever clip is there).`;

    public override customRenderer = SetMediaResultRenderer;

    private readonly recommendSceneAttributes: boolean;

    public constructor(args: SetMediaToolConstructorArgs) {
        super(args);
        this.recommendSceneAttributes = args.recommendSceneAttributes ?? true;
    }

    public override getToolDisplayName(): string {
        return 'Setting media';
    }

    // The resolved global index of the scene edited by the last successful execute(), used by
    // onEditApplied — the raw args may address the scene by scene_id rather than scene_index.
    private lastEditedSceneIndex: number | undefined;

    protected override onEditApplied(): void {
        // Changing media on the first scene changes the thumbnail.
        if (this.lastEditedSceneIndex === 0) {
            this.dispatch(refreshFirstScenePreviewOperation());
        }
    }

    public get statusIcon(): string {
        return 'image';
    }

    public async execute(args: SetMediaArgs, context: ToolExecutionContext) {
        const { asset_name, media_id } = args;
        const { parentActionGroup } = context;

        try {
            const { sceneIndex: scene_index, sceneShortId } = resolveSceneHandle(
                this.luminary,
                this.getState(),
                args,
            );
            this.lastEditedSceneIndex = scene_index;

            const scene = this.luminary.scenes[scene_index];
            const asset = scene.getAssetByName(asset_name);
            if (!asset) {
                return `Error: No asset found with name ${asset_name} in scene ${sceneShortId} which uses design ${scene.sceneDesign}`;
            }
            if (!asset.isEditable) {
                return nonEditableAssetError(asset_name, sceneShortId);
            }

            if (isNonRecommendable(scene, asset_name, this.getState())) {
                return nonEditableAssetError(asset_name, sceneShortId);
            }

            if (!VideoData.isMediaAsset(asset)) {
                return `Error: Asset ${asset_name} in scene ${sceneShortId} is not a media asset`;
            }

            // The presenter slot of a talking-head scene mirrors the scene group's recording
            // (linkToSceneGroup) and holds no media of its own. Giving it media severs that link,
            // so the scene plays a private copy of the recording from the top — the "video restarts
            // on every scene" defect. The editor never offers this slot to the media picker; refuse
            // it here for the same reason. Reframing still works through edit_media.
            if (asset.linkToSceneGroup) {
                return `Error: Asset ${asset_name} in scene ${sceneShortId} is the talking head — it mirrors the scene group's recording in sync with the speech and cannot be given other media. To show media over this part of the speech, add a scene with a b-roll design using ${TOOL_NAMES.addVoiceoverScene} (or use a different media slot on this scene). To reframe the speaker, call ${TOOL_NAMES.editMedia} with scale/position.`;
            }

            const mediaRegistry = MediaRegistry.getInstance();
            if (!mediaRegistry.hasItem(media_id)) {
                return `Error: Unknown media ${media_id}`;
            }
            const mediaSourceData = mediaRegistry.getMediaSourceData(media_id);
            if (!mediaSourceData) {
                return `Error: Unknown media ${media_id}`;
            }

            // A second copy of the group's own recording plays on its own clock from the start of
            // the file, out of sync with the speech. Linking the slot is the in-sync way to show it.
            const recordingUuid = scene.sceneGroup?.mediaAsset?.sourceData?.uuid;
            if (recordingUuid && mediaSourceData.uuid === recordingUuid) {
                return `Error: ${media_id} is this scene group's own recording. A copy of it in ${asset_name} would play from the start of the file, out of sync with the speech. To show a person from the recording in this slot, call ${TOOL_NAMES.editMedia} with frame_speaker instead: it links the slot to the recording, so it plays in sync.`;
            }

            try {
                asset.sourceData = mediaSourceData;
            } catch (error) {
                return `Error: data wrong format for asset ${asset_name} in scene ${sceneShortId}.`;
            }

            if (this.recommendSceneAttributes) {
                // Fetch and apply recommended scene attributes (crop, brightness, Ken Burns, text
                // position, watermark contrast) for the swapped-in media, matching what happens on a
                // manual media swap. Attributes the user has previously overridden (via edit_media)
                // are left alone. Bulk callers (e.g. the Media Optimizer) apply these in batch instead.
                const { fetchAndSetRecommendedSceneAttributes } = makeOperationsCallable(
                    { fetchAndSetRecommendedSceneAttributesOperation },
                    this.luminary,
                    this.dispatch,
                    this._getState,
                );
                await fetchAndSetRecommendedSceneAttributes({
                    sceneId: scene.id,
                    autoCrop: true,
                    recommendBrightness: true,
                    systemTriggered: true,
                    parentActionGroup,
                    autoScale: false,
                });
            }
            this.checkpointUnderTurn(parentActionGroup);

            this.recordSceneFill(scene_index, 'media');

            return `Success: set media for ${asset_name} to ${media_id} in scene ${sceneShortId}${this.buildReuseWarning(scene_index, media_id, mediaSourceData.uuid)}${buildAspectRatioNote(asset, asset_name)}`;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            return `Error setting media: ${errorMessage}`;
        }
    }

    /**
     * Warns when the media just applied also appears on other scenes — repeated
     * media reads as monotonous and causes ugly animation restarts, so reuse
     * should be a deliberate choice, not an accident.
     *
     * Matching is by source uuid rather than registry id: `getMediaIdFromAsset`
     * needs the MediaRegistry to reverse-map the uuid, and mid-build the registry
     * often hasn't indexed the media on the scenes already carrying it — those
     * assets resolve to "unregistered" and the reuse went unreported. The uuid is
     * on the asset itself, so it is available whatever the registry knows.
     */
    private buildReuseWarning(
        sceneIndex: number,
        mediaId: string | undefined,
        mediaUuid: string | undefined,
    ): string {
        if (!mediaId || !mediaUuid) {
            return '';
        }
        try {
            const otherSceneIndexes: number[] = [];
            this.luminary.scenes.forEach((scene, index) => {
                if (index === sceneIndex) {
                    return;
                }
                const hasSameMedia = scene
                    .getAssetsByType(VideoData.MEDIA_ASSET_TYPE)
                    .some((asset) => getMediaUuidFromAsset(asset) === mediaUuid);
                if (hasSameMedia) {
                    otherSceneIndexes.push(index);
                }
            });
            if (otherSceneIndexes.length === 0) {
                return '';
            }
            const sceneList = otherSceneIndexes.join(', ');
            return ` WARNING: media ${mediaId} already appears on scene${otherSceneIndexes.length > 1 ? 's' : ''} ${sceneList}. Repeating media makes the video monotonous and causes ugly animation restarts — use a different clip unless the repetition is deliberate.`;
        } catch {
            // The warning is best-effort advice; never fail the set over it.
            return '';
        }
    }
}

// Roughly 36% of the media left on screen. Below this the crop is ordinary framing — a half-canvas
// panel holding a 16:9 clip sits at 2x and is usually deliberate — so staying quiet keeps the note
// meaningful when it does appear. Starting point; worth revisiting once we see how it reads.
const ASPECT_RATIO_MISMATCH_THRESHOLD = 2.75;

/**
 * Deliberately a note rather than an error: a tight crop is sometimes exactly what the design
 * wants, and the agent is the one positioned to tell an intentional close-up from an accident.
 */
const buildAspectRatioNote = (asset: TimeAwareMediaAsset, assetName: string): string => {
    try {
        const fit = getMediaFrameFit(asset);
        if (!fit || fit.mismatch < ASPECT_RATIO_MISMATCH_THRESHOLD) {
            return '';
        }

        const visiblePercent = Math.round(fit.visibleFraction * 100);
        const mediaShape = describeAspectRatio(fit.mediaAspect);
        const frameShape = describeAspectRatio(fit.frameAspect);

        return ` ASPECT RATIO: the media is ${mediaShape} (${fit.mediaAspect.toFixed(2)}:1) but ${assetName} is ${frameShape} (${fit.frameAspect.toFixed(2)}:1), so only about ${visiblePercent}% of the media is visible — it is cropped to fill the frame and the subject may be cut off. If that crop is deliberate, keep it. Otherwise pick media shaped like the frame, use a scene design that fits this media, or set position/scale to bring the subject back into view.`;
    } catch {
        // The note is best-effort advice; never fail the set over it.
        return '';
    }
};

const SetMediaResultRenderer: React.FC<CustomRendererComponentProps> = ({
    isLoading,
    result,
    toolCall,
}) => {
    if (isLoading) {
        return null;
    }
    const args = toolCall ? JSON.parse(toolCall.function.arguments) : {};
    const mediaId = args.media_id;
    const mediaRegistry = MediaRegistry.getInstance();
    const mediaData = mediaId ? mediaRegistry.getItem(mediaId) : null;
    return (
        <div className={chatMessageTextStyles['chat-message-text']}>
            <MarkdownWithDiff>{result}</MarkdownWithDiff>
            {mediaData && (
                <div>
                    {React.createElement(BackgroundAsset, {
                        searchContext: null,
                        media: mediaData,
                        media_id: 0,
                        result_index: 0,
                    })}
                </div>
            )}
        </div>
    );
};
